import {
  Descriptions,
  Badge,
  Button,
  Row,
  Col,
  Card as AntdCard,
  Collapse,
  Tag,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import React from "react";
import {
  Beneficiary,
  Customer,
  Subscription,
} from "../helpers/shared-interfaces";
import { formatter, generateNameFromCustomer } from "../helpers/utilities";
import CardSection from "./CardSection";
import { tabPaneStyle } from "../helpers/constants";
import EntitledSection from "./EntitledSection";
import entitlements from "../config/entitlements";

interface Props {
  customer: Customer;
}

const { Panel } = Collapse;

export const CustomerBeneficiaryCard = (
  beneficiary: Beneficiary,
): JSX.Element => {
  return (
    <div>
      <CardSection
        headingText="Name"
        data={generateNameFromCustomer(beneficiary as unknown as Customer)}
      />

      {beneficiary.idType && (
        <CardSection headingText="ID Type" data={beneficiary.idType.name} />
      )}

      {beneficiary.idNumber && (
        <CardSection headingText="ID Number" data={beneficiary.idNumber} />
      )}

      {beneficiary.dateOfBirth && (
        <CardSection
          headingText="Date of Birth"
          data={beneficiary.dateOfBirth}
        />
      )}

      {!!beneficiary.benefitsPercentage && (
        <CardSection
          headingText="Benefits Percentage"
          data={`${String(beneficiary.benefitsPercentage)}%`}
        />
      )}

      {!!beneficiary.beneficiaryAmount && (
        <CardSection
          headingText="Benefits Amount"
          data={formatter.format(beneficiary.beneficiaryAmount)}
        />
      )}

      {beneficiary.emailAddress && (
        <CardSection
          headingText="Email"
          data={String(beneficiary.emailAddress)}
        />
      )}

      {beneficiary.customerRelationship && (
        <CardSection
          headingText="Relationship"
          data={String(beneficiary.customerRelationship.name)}
        />
      )}

      {beneficiary.phoneNumber && (
        <CardSection
          headingText="Phone Number"
          data={String(beneficiary.phoneNumber)}
        />
      )}

      {beneficiary.guardianName && (
        <CardSection
          headingText="Guardian Name"
          data={String(beneficiary.guardianName)}
        />
      )}

      {beneficiary.guardianEmail && (
        <CardSection
          headingText="Guardian Email"
          data={String(beneficiary.guardianEmail)}
        />
      )}

      {beneficiary.guardianPhoneNumber && (
        <CardSection
          headingText="Guardian Phone Number"
          data={String(beneficiary.guardianPhoneNumber)}
        />
      )}

      {beneficiary.guardianAddress && (
        <CardSection
          headingText="Guardian Address"
          data={String(beneficiary.guardianAddress)}
        />
      )}

      {beneficiary.beneficiaryType && (
        <CardSection
          headingText="Beneficiary Type"
          data={String(beneficiary.beneficiaryType)}
        />
      )}

      {beneficiary.institutionName && (
        <CardSection
          headingText="Institution Name"
          data={String(beneficiary.institutionName)}
        />
      )}

      {beneficiary.institutionAddress && (
        <CardSection
          headingText="Institution Address"
          data={String(beneficiary.institutionAddress)}
        />
      )}

      {beneficiary.institutionPhoneNumber && (
        <CardSection
          headingText="Institution Phone"
          data={String(beneficiary.institutionPhoneNumber)}
        />
      )}

      {beneficiary.ghanaCardUrl && (
        <CardSection
          headingText="Ghana Card"
          data={
            <a
              href={beneficiary.ghanaCardUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ width: "100%", display: "inline-block", overflowWrap: "break-word" }}            >
              {beneficiary.ghanaCardUrl}
            </a>
          }
        />
      )}

      {beneficiary.ghanaCardBackUrl && (
        <CardSection
          headingText="Ghana Card Back"
          data={
            <a
              href={beneficiary.ghanaCardBackUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{ width: "100%", display: "inline-block", overflowWrap: "break-word" }}            >
              {beneficiary.ghanaCardBackUrl}
            </a>
          }
        />
      )}

      {beneficiary.idExpiryDate && (
        <CardSection
          headingText="Ghana Card Expiry Date"
          data={String(beneficiary.idExpiryDate)}
        />
      )}

      {beneficiary.idIssueDate && (
        <CardSection
          headingText="Ghana Card Issue Date"
          data={String(beneficiary.idIssueDate)}
        />
      )}

      {!beneficiary.isActive && (
        <Tag
          color="#002c6d"
          style={{
            marginBottom: "2px",
          }}
        >
          Previous
        </Tag>
      )}
    </div>
  );
};

export default (props: {
  subscriptions: Subscription[];
  style?: React.CSSProperties;
  onAddBeneficiary?: () => void;
  onEditBeneficiary?: (beneficiary: Beneficiary) => void;
  onDeleteBeneficiary?: (beneficiary: Beneficiary) => void;
}): JSX.Element => {
  return (
    <div
      style={{
        border: "1px solid rgb(232 232 232)",
        padding: "20px",
        boxSizing: "border-box",
        ...props.style,
      }}
    >
      <Descriptions
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span>Customer Beneficiaries</span>
            <EntitledSection entitlement={entitlements.Customers.edit}>
              {props.onAddBeneficiary && (
                <Button
                  className="base-button"
                  onClick={props.onAddBeneficiary}
                  icon={<PlusOutlined />}
                  style={{ marginLeft: "auto" }}
                >
                  Add Beneficiary
                </Button>
              )}
            </EntitledSection>
          </div>
        }
      ></Descriptions>

      <Collapse>
        {props.subscriptions
          .filter(
            (subscription) =>
              subscription.customerBeneficiaries &&
              subscription.customerBeneficiaries.length > 0,
          )
          .map((subscription: Subscription): JSX.Element => {
          return (
            <Panel
              header={
                <span>
                  {subscription.product && subscription.product.name}
                  <Badge
                    count={
                      subscription.customerBeneficiaries &&
                      subscription.customerBeneficiaries.length
                    }
                    style={tabPaneStyle}
                  />
                </span>
              }
              key={subscription.id}
            >
              <Row gutter={[16, 16]}>
                {subscription.customerBeneficiaries &&
                  subscription.customerBeneficiaries.map(
                    (beneficiary: Beneficiary): JSX.Element => {
                      return (
                        <Col key={beneficiary.id} span={6}>
                      <AntdCard
                        className="associated-entity-card"
                        key={beneficiary.id}
                      >
                        {CustomerBeneficiaryCard(beneficiary)}
                        {beneficiary.isActive && !beneficiary.deletedAt &&
                          !(beneficiary as unknown as { deletedAt?: string })
                            .deletedAt && (
                            <div
                              style={{ display: "flex", gap: 8, marginTop: 10 }}
                            >
                              <EntitledSection
                                entitlement={entitlements.Customers.edit}
                              >
                                {props.onEditBeneficiary &&
                                  subscription.product?.isActive !== false && (
                                    <Button
                                      icon={<EditOutlined />}
                                      aria-label="edit-beneficiary"
                                      onClick={() =>
                                        props.onEditBeneficiary?.(beneficiary)
                                      }
                                      style={actionButtonStyle}
                                    ></Button>
                                  )}
                              </EntitledSection>
                              <EntitledSection
                                entitlement={entitlements.Customers.edit}
                              >
                                {props.onDeleteBeneficiary && (
                                  <Button
                                    icon={<DeleteOutlined />}
                                    aria-label="delete-beneficiary"
                                    onClick={() =>
                                      props.onDeleteBeneficiary?.(beneficiary)
                                    }
                                    danger
                                  >
                                
                                  </Button>
                                )}
                              </EntitledSection>
                            </div>
                          )}
                      </AntdCard>
                    </Col>
                  );
                },
                  )}
              </Row>
            </Panel>
          );
        })}
      </Collapse>
    </div>
  );
};
const actionButtonStyle: React.CSSProperties = {
  borderStyle: "dashed",
};
